import { Alert, AlertTitle, Box, Button, Divider, LinearProgress, Typography } from '@mui/material';
import { isToday } from 'date-fns';

import { sapStatus, type Veranstaltung, type ZoomMeetingInfo } from '../../../dtos';
import { trpc } from '../../../trpc';
import { isMeetingInSync } from './IsMeetingInSync';
import { retriggerMessages } from './ZoomRetriggerComponent.content';

export type ZoomRetriggerVA = Pick<
  Veranstaltung,
  'sapid' | 'sapStatus' | 'ablauf' | 'experteBlockungen' | 'virtualclassroom'
>;

type ZoomRetriggerProps = {
  readonly veranstaltung: ZoomRetriggerVA;
  readonly retriggerSucceeded: (newZoomMeeting: ZoomMeetingInfo) => void;
};

const computeRetriggerAction = (veranstaltung: ZoomRetriggerVA): { isRetriggerEnabled: boolean; text: string } => {
  const ablaufForToday = findAblaufForToday(veranstaltung.ablauf);
  const zoomMeetingForToday = getZoomMeetingForToday(veranstaltung.virtualclassroom);
  const isFixiert = veranstaltung.sapStatus === sapStatus.FIXIERT;
  const isReferentPresent = veranstaltung.experteBlockungen.length > 0;

  if (!ablaufForToday) {
    return { isRetriggerEnabled: false, text: retriggerMessages.NO_MEETING_FOR_TODAY };
  }

  if (!isFixiert) {
    return { isRetriggerEnabled: false, text: retriggerMessages.VERANSTALTUNG_NOT_FIXED };
  }

  if (!isReferentPresent) {
    return { isRetriggerEnabled: false, text: retriggerMessages.VERANSTALTUNG_HAS_NO_REFERENT };
  }

  if (!zoomMeetingForToday) {
    return { isRetriggerEnabled: true, text: retriggerMessages.MEETING_FOR_TODAY_AUTO_CREATION_FAILED };
  }

  if (!isMeetingInSync(zoomMeetingForToday, veranstaltung.experteBlockungen, ablaufForToday)) {
    return { isRetriggerEnabled: true, text: retriggerMessages.MEETING_FOR_TODAY_IS_OUT_OF_SYNC };
  }

  return { isRetriggerEnabled: false, text: retriggerMessages.MEETING_FOR_TODAY_IS_IN_SYNC };
};

export const ZoomRetriggerComponent = ({ veranstaltung, retriggerSucceeded }: ZoomRetriggerProps) => {
  const zoomRetriggerMutation = trpc.zoom.retriggerZoom.useMutation();

  const { isRetriggerEnabled, text } = computeRetriggerAction(veranstaltung);

  const handleRetrigger = async () => {
    const ablaufForToday = findAblaufForToday(veranstaltung.ablauf);
    if (ablaufForToday) {
      await zoomRetriggerMutation.mutateAsync(
        { sapId: veranstaltung.sapid, date: ablaufForToday.starttime.toJSON().split('T')[0] },
        {
          onSuccess: (data) => {
            retriggerSucceeded(data);
          },
        },
      );
    }
  };

  if (zoomRetriggerMutation.isPending) {
    return <LinearProgress />;
  }

  if (zoomRetriggerMutation.isSuccess) {
    return (
      <Alert severity="success">
        <AlertTitle>Zoom-Meeting wurde angelegt</AlertTitle>
      </Alert>
    );
  }

  return (
    <>
      <Box>
        <Typography variant="paragraph">{text}</Typography>
        {isRetriggerEnabled && (
          <>
            <Divider sx={{ mt: 1 }} />
            <Button variant="contained" size="small" sx={{ marginTop: 2 }} onClick={handleRetrigger}>
              Zoom Meeting für heute neu anlegen
            </Button>
          </>
        )}
      </Box>
      {zoomRetriggerMutation.isError && (
        <>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Alert severity="error">
            <AlertTitle>Fehler beim Anlegen des Zoom-Meetings</AlertTitle>
            {zoomRetriggerMutation.error.message}
          </Alert>
        </>
      )}
    </>
  );
};

function getZoomMeetingForToday(virtualClassroom: Veranstaltung['virtualclassroom']): ZoomMeetingInfo | undefined {
  const zoomDetails = virtualClassroom?.details ?? [];
  return zoomDetails.find((zoomMeeting: ZoomMeetingInfo) => isToday(new Date(zoomMeeting.startTime)));
}

function findAblaufForToday(ablauf: Veranstaltung['ablauf']) {
  return ablauf
    .map((ablaufTag) => ({
      ...ablaufTag,
      starttime: new Date(ablaufTag.starttime),
      endtime: new Date(ablaufTag.endtime),
    }))
    .find((ablaufTag) => isToday(ablaufTag.starttime));
}
